import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Database, Loader2, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail))
        setError(detail.map((d) => d.msg || JSON.stringify(d)).join(" "));
      else setError("Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="w-full lg:w-[480px] flex flex-col justify-center px-8 md:px-16 bg-[#0A0A0A]">
        <div className="w-full max-w-[360px] mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <Database size={28} className="text-[#0066FF]" />
            <span
              className="text-2xl font-bold text-white"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              ReportHub
            </span>
          </div>

          <h1
            className="text-3xl font-bold tracking-tight text-white mb-2"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Sign in
          </h1>
          <p className="text-sm text-[#A3A3A3] mb-8">
            Internal reporting platform
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div
                className="flex items-center gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                data-testid="login-error"
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3]"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                data-testid="login-email-input"
                className="bg-[#0A0A0A] border-[#262626] focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] h-10"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3]"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                data-testid="login-password-input"
                className="bg-[#0A0A0A] border-[#262626] focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] h-10"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#0066FF] hover:bg-[#005CE6] text-white h-10 font-semibold"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : null}
              Sign in
            </Button>
          </form>
        </div>
      </div>

      {/* Right - Image */}
      <div
        className="hidden lg:block flex-1 relative"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/3612930/pexels-photo-3612930.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2
            className="text-3xl font-bold text-white mb-3"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            SAP B1 HANA Reporting
          </h2>
          <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-md">
            Extensible reporting platform with dynamic report creation,
            role-based access control, and real-time data from SAP Business One.
          </p>
        </div>
      </div>
    </div>
  );
}
